import React from 'react';
import { Select } from 'antd';
import {DownOutlined} from "@ant-design/icons";
import { t } from '../../../i18n';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';

interface OptionType {
    label: string;
    value: string | number | boolean;
    /** 覆盖选项的原生 hover tooltip；不传时 rc-select 默认用 label */
    title?: string;
}

interface FilterItemProps {
    label: string;
    options: string[] | OptionType[];
    value?: any;
    onChange?: (value: any) => void;
    className?: string;
    spanClassName?: string;
    disabled?: boolean;
    mode?: 'multiple' | 'tags' | undefined;
    showSearch?:boolean;
    placeholder?:string;
    loading?: boolean;
    notFoundContent?: React.ReactNode;
    maxTagCount?: number | 'responsive';
    allowClear?: boolean;
    /** 受控下拉展开（传给 antd Select / rc-select） */
    open?: boolean;
    onDropdownVisibleChange?: (open: boolean) => void;
    /** 远程搜索回调；传入时表示选项由后端按关键词返回，需配合 filterOption={false} 关闭本地过滤 */
    onSearch?: (keyword: string) => void;
    /** 覆盖默认的本地过滤；远程搜索场景传 false，避免已由后端筛选过的选项被再次本地过滤掉 */
    filterOption?: boolean;
    /** tags 模式下触发自动分词的分隔符列表 */
    tokenSeparators?: string[];
}

const ParcelFilterStyle: React.FC<FilterItemProps> = ({label, options, value, onChange, className, spanClassName, disabled = false, mode, showSearch=true, placeholder=null, loading, notFoundContent, maxTagCount, allowClear = true, open, onDropdownVisibleChange, onSearch, filterOption, tokenSeparators}) => {
    // 判断options是简单字符串数组还是对象数组
    const isObjectOptions = options.length > 0 && typeof options[0] !== 'string';

    const handleChange = (selectedValue: any) => {
        if (onChange) onChange(selectedValue);
    };

    const renderOptions = React.useMemo(() => {
        if (options.length === 0) return [];
        return typeof options[0] === 'object'
            ? (options as OptionType[])
                .filter(option => option?.label !== undefined && option?.label !== null && option?.value !== undefined && option?.value !== null)
                .map(option => ({
                    label: option.label,
                    value: String(option.value),
                    title: option.title
                }))
            : (options as string[])
                .filter(option => option !== undefined && option !== null)
                .map(option => ({
                    label: option,
                    value: option
                }));
    }, [options]);

    // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
    const containerClassName = useFilterContainerClassName(className);
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>{label}</span>
            <div className={inputContainerClassName}>
                <Select
                    mode={mode}
                    value={value as any}
                    onChange={handleChange}
                    open={open}
                    onDropdownVisibleChange={onDropdownVisibleChange}
                    className="w-full"
                    size="middle"
                    placeholder={placeholder !== null && placeholder !== undefined ? placeholder : t('components.input.all')}
                    allowClear={allowClear}
                    suffixIcon={<DownOutlined />}
                    disabled={disabled}
                    loading={loading}
                    options={renderOptions}
                    showSearch={showSearch}
                    maxTagCount={maxTagCount}
                    optionFilterProp="label"
                    onSearch={onSearch}
                    filterOption={
                        filterOption !== undefined
                            ? filterOption
                            : (input, option) =>
                                option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                    notFoundContent={notFoundContent}
                    tokenSeparators={tokenSeparators}
                >
                </Select>
            </div>
        </div>
    );
};

export default ParcelFilterStyle;
